import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { COLORS, PRODUCT, formatPrice, productUrl } from "../../data/catalog";
import { EASE } from "../../lib/api";
import { FadeUp, MaskLines } from "../site/Reveal";
import { Swatches } from "../site/Swatches";

export const ColorSuite = ({ onQuickView }) => {
  const [i, setI] = useState(0);
  const c = COLORS[i];
  const pick = (id) => setI(COLORS.findIndex((x) => x.id === id));

  return (
    <section id="collection" data-testid="color-suite" className="scroll-mt-24 bg-ivory py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="mb-12 flex flex-col justify-between gap-6 md:mb-16 md:flex-row md:items-end">
          <div>
            <p className="label-mono">Featured collection · 06 colors</p>
            <MaskLines inView lines={["Six signature", <em key="c">colors.</em>]} className="mt-4 font-display text-5xl font-light leading-[0.92] text-ink md:text-7xl lg:text-8xl" />
          </div>
          <FadeUp className="max-w-sm text-sm leading-relaxed text-graphite">One silhouette, six moods. Select a color and watch the bag change in real time.</FadeUp>
        </div>

        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
          <FadeUp className="lg:col-span-7">
            <div className="relative aspect-[5/4] overflow-hidden bg-white" data-testid="color-suite-stage">
              <motion.div className="absolute inset-0" animate={{ background: `radial-gradient(55% 55% at 50% 58%, ${c.hex}40, transparent 72%)` }} transition={{ duration: 0.9 }} />
              <span className="absolute left-5 top-5 z-10 font-mono text-xs text-graphite">{String(i + 1).padStart(2, "0")} / 06</span>
              <AnimatePresence initial={false}>
                <motion.img key={c.id} src={c.image} alt={`${PRODUCT.name} in ${c.name}`}
                  initial={{ opacity: 0, x: 60, scale: 1.04 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: -60, scale: 0.97 }}
                  transition={{ duration: 0.9, ease: EASE }} className="absolute inset-0 h-full w-full object-contain p-6 mix-blend-multiply md:p-14" />
              </AnimatePresence>
            </div>
          </FadeUp>
          <div className="lg:col-span-5">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5, ease: EASE }}>
                <p className="label-mono">Now viewing</p>
                <h3 className="mt-3 font-display text-5xl font-light text-ink md:text-6xl" data-testid="color-suite-name">{c.name}</h3>
                <p className="mt-4 max-w-sm text-base leading-relaxed text-graphite">{c.mood}</p>
              </motion.div>
            </AnimatePresence>
            <div className="mt-8"><Swatches value={c.id} onChange={pick} prefix="color-suite" size="lg" /></div>
            <div className="mt-10 flex items-center gap-6 border-t border-line pt-8">
              <span className="text-lg text-ink">{formatPrice(PRODUCT.price)}</span>
              <Link to={productUrl(c.id)} data-testid="color-suite-shop-button" className="btn-dark group">Shop {c.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              <button onClick={() => onQuickView(c.id)} data-testid="color-suite-quick-view" className="link-line text-xs uppercase tracking-[0.2em] text-ink">Quick view</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
